import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { PLUGIN_CATEGORY } from '@/plugin/pluginHost.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { PluginState, SelectedRegionOptionTarget } from '@/plugin/pluginApiDefinition';
import type { PluginCapsuleRegion } from '@/plugin/pluginHost.constants';
import type { PluginOutputV1 } from '@seeqdev/api/model/PluginOutputV1';

type PluginStateMap = Partial<Record<string, PluginState | null>>;
type PluginContentDataValue = Record<string, unknown> | string | number | boolean | null;
type PluginContentDataMap = Partial<Record<string, PluginContentDataValue>>;
type PluginContentRenderMap = Partial<Record<string, boolean>>;

export interface PluginStoreState {
  plugins: PluginOutputV1[];
  pluginState: PluginStateMap;
  displayPaneRenderComplete: boolean;
  queryParam?: string;
  currentEntryPoint?: string;
  contentPluginData: PluginContentDataMap;
  contentPluginRenderComplete: PluginContentRenderMap;
  selectedRegionOptionTarget: SelectedRegionOptionTarget | null;
  capsuleRegion: PluginCapsuleRegion | null;
}

export type PluginStoreDehydratedState = Omit<
  PluginStoreState,
  | 'plugins'
  | 'displayPaneRenderComplete'
  | 'queryParam'
  | 'contentPluginData'
  | 'contentPluginRenderComplete'
  | 'capsuleRegion'
  | 'selectedRegionOptionTarget'
>;

export class PluginStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqPluginStore';

  initialize(): void {
    const existingPlugins = (this.state?.get?.('plugins') ?? []) as PluginOutputV1[];
    const initialState: PluginStoreState = {
      plugins: existingPlugins,
      pluginState: {},
      displayPaneRenderComplete: true,
      queryParam: undefined,
      currentEntryPoint: undefined,
      contentPluginData: {},
      contentPluginRenderComplete: {},
      selectedRegionOptionTarget: null,
      capsuleRegion: null,
    };

    this.state = this.immutable(initialState);
  }

  get plugins(): PluginOutputV1[] {
    return this.state.get('plugins');
  }

  get pluginState(): PluginStateMap {
    return this.state.get('pluginState');
  }

  get selectedRegionOptionTarget(): SelectedRegionOptionTarget | null {
    return this.state.get('selectedRegionOptionTarget');
  }

  get capsuleRegion(): PluginCapsuleRegion | null {
    return this.state.get('capsuleRegion');
  }

  get displayPaneRenderComplete(): boolean {
    return this.state.get('displayPaneRenderComplete');
  }

  get queryParam(): string | undefined {
    return this.state.get('queryParam');
  }

  get contentPluginData(): PluginContentDataMap {
    return this.state.get('contentPluginData');
  }

  get contentPluginRenderComplete(): PluginContentRenderMap {
    return this.state.get('contentPluginRenderComplete');
  }

  displayPanePlugins(): PluginOutputV1[] {
    return _.filter(this.state.get('plugins'), {
      category: PLUGIN_CATEGORY.DISPLAY_PANE,
    }) as PluginOutputV1[];
  }

  homeScreenPlugins(): PluginOutputV1[] {
    return _.filter(this.state.get('plugins'), {
      category: PLUGIN_CATEGORY.HOME_SCREEN,
    }) as PluginOutputV1[];
  }

  /**
   * Gets a plugin by its pluginIdentifier from the store
   *
   * @param {string} identifier - the plugin identifier
   */
  getPlugin(identifier: string): PluginOutputV1 | undefined {
    return _.chain(this.state.get('plugins')).filter({ identifier }).first().value() as PluginOutputV1 | undefined;
  }

  dehydrate(): PluginStoreDehydratedState {
    return _.omit(this.state.serialize() as PluginStoreState, [
      'plugins',
      'displayPaneRenderComplete',
      'queryParam',
      'contentPluginData',
      'contentPluginRenderComplete',
      'capsuleRegion',
      'selectedRegionOptionTarget',
    ]) as PluginStoreDehydratedState;
  }

  rehydrate(dehydratedState: Partial<PluginStoreDehydratedState>): void {
    this.state.deepMerge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Sets the list of plugins into the store
     *
     * @param {Object} payload - Object container for arguments
     * @param {Object[]} payload.plugins - the new list of plugins to be set into the store
     */
    PLUGINS_SET: (payload: { plugins: PluginOutputV1[] }): void => this.state.set('plugins', payload.plugins),
    /**
     * Sets the plugin state for a given plugin into the store
     *
     * @param {Object} payload - Object container for arguments
     * @param {string} payload.pluginIdentifier - the identifier of the plugin
     * @param {Object} payload.pluginState - the new state
     */
    PLUGINS_SET_STATE: (payload: { pluginIdentifier: string; pluginState: PluginState | null }): void =>
      this.state.set(['pluginState', payload.pluginIdentifier], payload.pluginState),
    /**
     * Sets the display pane render complete into the store
     *
     * @param {Object} payload - Object container for arguments
     * @param {boolean} payload.complete - if the display pane render is complete
     */
    PLUGINS_SET_DISPLAY_PANE_RENDER_COMPLETE: (payload: { complete: boolean }): void =>
      this.state.set('displayPaneRenderComplete', payload.complete),

    /**
     * Sets the HomeScreen add-on query parameter into the store, so it can be passed to the plugin when it loads
     *
     * @param {Object} payload - Object container for arguments
     * @param {string} payload.queryParam - the query parameter
     */
    PLUGINS_SET_QUERY_PARAM: (payload: { queryParam?: string }): void =>
      this.state.set('queryParam', payload.queryParam),

    /**
     * Tells the store whether or not the content plugin has finished rendering, which is used by Organizer to
     * determine when the content is finished loading.
     */
    PLUGINS_SET_CONTENT_PLUGIN_RENDER_COMPLETE: (payload: { contentId: string; complete: boolean }): void =>
      this.state.set(['contentPluginRenderComplete', payload.contentId], payload.complete),

    /**
     * Updates the current data for a content plugin. This cannot be passed directly to the plugin iframe as the
     * plugin requests it on its own schedule.
     */
    PLUGINS_UPDATE_CONTENT_PLUGIN_DATA: (payload: { contentId: string; data?: PluginContentDataValue }): void => {
      if (payload.data) {
        this.state.set(['contentPluginData', payload.contentId], payload.data);
      } else {
        this.state.unset(['contentPluginData', payload.contentId]);
      }
    },

    /**
     * Set selected region selected option.
     */
    PLUGINS_SET_SELECTED_REGION_TARGET: (payload: SelectedRegionOptionTarget): void =>
      this.state.set('selectedRegionOptionTarget', payload),

    /**
     * Set selected capsule for plugin options.
     */
    PLUGINS_SET_CAPSULE_REGION: (payload: { capsuleRegion: PluginCapsuleRegion }): void =>
      this.state.set('capsuleRegion', payload.capsuleRegion),
  };
}
